import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import {FormBuilder, FormGroup} from "@angular/forms";
import { JaarrapportageEntity,
  ClientEntity,
  Jaarrekening, Jaarrapportage, NonNegativeDecimalPeriodValues } from "@snelstart-ventures/fiscaal/domain-logic";
import {BokkzAlgDataService, BokkzJrDataService} from "@snelstart-ventures/fiscaal/domain-logic";
import { combineLatest, distinctUntilChanged, Observable, ReplaySubject, Subject, takeUntil } from "rxjs";
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SpecFormattedexplanationComponent } from '../spec-formattedexplanation/spec-formattedexplanation.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-toelichting-gemiddeld-aantal-werknemers',
  templateUrl: './toelichting-gemiddeld-aantal-werknemers.component.html',
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SpecFormattedexplanationComponent,
    CommonModule
  ],
  styleUrls: ['./toelichting-gemiddeld-aantal-werknemers.component.css']
})
export class ToelichtingGemiddeldAantalWerknemersComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public id!: string;
  @Input() selectedTabObservable!: Observable<number>;
  private selectedTabSubject = new ReplaySubject<number>(1);
  showCustomControls = true;
  private destroy$ = new Subject<void>();
  public selectedTab = 0;
  public index = 0;
  public dialogVisible = false;
  public clientEntity = {} as ClientEntity;
  public jrEntity = {} as JaarrapportageEntity;
  public isRechtspersoon = false;

  public averageNumberEmployeesFg = {} as FormGroup;
  public averageNumberEmployeesNetherlandsFg = {} as FormGroup;
  public averageNumberEmployeesAbroadFg = {} as FormGroup;
  public averageNumberEmployeesDisclosureFg = {} as FormGroup;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  constructor(
    public algDataService: BokkzAlgDataService,
    private cdRef: ChangeDetectorRef,
    public jrDataService: BokkzJrDataService,
    private fb: FormBuilder) {
    this.createFormGroups();
  }

  ngOnInit(): void {
    this.algDataService.client.subscribe((value) => {
      this.clientEntity = value;
    });
    if (this.selectedTabObservable) {
      this.selectedTabObservable
        .pipe(takeUntil(this.destroy$))
        .subscribe(tab => {
          this.selectedTabSubject.next(tab);
        });
    }

    combineLatest([
      this.selectedTabSubject.pipe(distinctUntilChanged()),
      this.jrDataService.jaarrapportageEntity.pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([tab, jrEntity]) => {
        this.selectedTab = tab;
        this.jrEntity = jrEntity;
        this.patchData(jrEntity);
      });

    this.jrDataService.isRechtspersoon.subscribe((value) => {
      this.isRechtspersoon = value;
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTabObservable']) {
      this.selectedTabObservable = changes['selectedTabObservable'].currentValue;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public showDialog(input: number) {
    this.index = input
    this.dialogVisible = true;
  }

  public createFormGroups() {

    this.averageNumberEmployeesNetherlandsFg = this.fb.group<NonNegativeDecimalPeriodValues>({
      valuePeriod1: null as unknown as number,
      valuePeriod2: null as unknown as number,
    })
    this.averageNumberEmployeesAbroadFg = this.fb.group<NonNegativeDecimalPeriodValues>({
      valuePeriod1: null as unknown as number,
      valuePeriod2: null as unknown as number,
    })
    this.averageNumberEmployeesFg = this.fb.group<NonNegativeDecimalPeriodValues>({
      valuePeriod1: null as unknown as number,
      valuePeriod2: null as unknown as number,
    })

    this.averageNumberEmployeesDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
  }

  private patchData(value: JaarrapportageEntity) {

    const gemiddeldAantalWerknemers = value.rapportage?.jaarrekeningen?.at(0)?.toelichting?.overigeToelichtingen?.gemiddeldAantalWerknemers;

    if (gemiddeldAantalWerknemers) {

      const uitsplitsing = gemiddeldAantalWerknemers.gemiddeldAantalWerknemersUitsplitsing;
      const tekstueel = gemiddeldAantalWerknemers.gemiddeldAantalWerknemersTekstueel;

      if (tekstueel) {
        if (tekstueel.averageNumberEmployeesDisclosure) {
          this.averageNumberEmployeesDisclosureFg.patchValue(tekstueel.averageNumberEmployeesDisclosure);
        }
      }
      if (uitsplitsing) {
        if (uitsplitsing.averageNumberEmployeesNetherlands) {
          this.averageNumberEmployeesNetherlandsFg.patchValue(uitsplitsing.averageNumberEmployeesNetherlands,{ emitEvent: false })
        }
        if (uitsplitsing.averageNumberEmployeesAbroad) {
          this.averageNumberEmployeesAbroadFg.patchValue(uitsplitsing.averageNumberEmployeesAbroad,{ emitEvent : false })
        }
      }

    }
  }

  public getTotalen() {
    const nederlandPeriod1 = this.averageNumberEmployeesNetherlandsFg.get('valuePeriod1')?.value
    const nederlandPeriod2 = this.averageNumberEmployeesNetherlandsFg.get('valuePeriod2')?.value
    const buitenlandPeriod1 = this.averageNumberEmployeesAbroadFg.get('valuePeriod1')?.value
    const buitenlandPeriod2 = this.averageNumberEmployeesAbroadFg.get('valuePeriod2')?.value

    const valuePeriod1 = (nederlandPeriod1 == null && buitenlandPeriod1 == null) ? null : (nederlandPeriod1 ?? 0) + (buitenlandPeriod1 ?? 0);
    const valuePeriod2 = (nederlandPeriod2 == null && buitenlandPeriod2 == null) ? null : (nederlandPeriod2 ?? 0) + (buitenlandPeriod2 ?? 0);

    this.averageNumberEmployeesFg.patchValue({valuePeriod1: valuePeriod1, valuePeriod2: valuePeriod2},{emitEvent:false})

    return true;
  }

  saveForm(): void {
    this.averageNumberEmployeesFg.updateValueAndValidity({ emitEvent: false });
    this.averageNumberEmployeesNetherlandsFg.updateValueAndValidity({ emitEvent: false });
    this.averageNumberEmployeesAbroadFg.updateValueAndValidity({ emitEvent: false });
    this.averageNumberEmployeesDisclosureFg.updateValueAndValidity({ emitEvent: false });

    if (this.jrEntity && this.jrEntity.rapportage && this.jrEntity.rapportage.jaarrekeningen) {
      this.jrEntity.rapportage.jaarrekeningen[0] = {
        ...this.jrEntity.rapportage.jaarrekeningen[0],
        toelichting: {
          ...this.jrEntity.rapportage.jaarrekeningen[0].toelichting,
          overigeToelichtingen: {
            ...this.jrEntity.rapportage.jaarrekeningen[0].toelichting?.overigeToelichtingen,
            gemiddeldAantalWerknemers: {
              ...this.jrEntity.rapportage.jaarrekeningen[0].toelichting?.overigeToelichtingen?.gemiddeldAantalWerknemers,
              gemiddeldAantalWerknemersUitsplitsing: {
                ...this.jrEntity.rapportage.jaarrekeningen[0].toelichting?.overigeToelichtingen?.gemiddeldAantalWerknemers?.gemiddeldAantalWerknemersUitsplitsing,
                averageNumberEmployeesNetherlands: this.averageNumberEmployeesNetherlandsFg.value,
                averageNumberEmployeesAbroad: this.averageNumberEmployeesAbroadFg.value,
              },
              gemiddeldAantalWerknemersTekstueel: {
                ...this.jrEntity.rapportage.jaarrekeningen[0].toelichting?.overigeToelichtingen?.gemiddeldAantalWerknemers?.gemiddeldAantalWerknemersTekstueel,
                averageNumberEmployeesDisclosure: this.averageNumberEmployeesDisclosureFg.value,
              }
            },
          }
        }
      } as Jaarrekening
    }

    this.jrEntity = {
      ...this.jrEntity,
      rapportage: {
        ...this.jrEntity.rapportage,
        jaarrekeningen: [this.jrEntity.rapportage?.jaarrekeningen?.at(0)]
      }
    } as Jaarrapportage
    this.jrDataService.updateEntity(this.jrEntity);
  }
}

